// MOBIL ALSÓ NAV — a partner-felület fő navigációja telefonon (5 elem + jelvény).
//
// MIÉRT ALSÓ NAV (a belső felületen NINCS ilyen): a partner négy lapot használ, jellemzően telefonról, és a
// hüvelykujj-zónában kell lennie a váltásnak. A belső felület deszktop-domináns, ott a sidebar + FAB a minta
// (`docs/foundations/design.md` §9).
import type { PortalMeResponse } from '@atlas/contracts';
import { UserIcon } from '@heroicons/react/24/outline';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { visiblePortalNav } from '@/components/portal/portal-nav';
import { cn } from '@/lib/utils';

export function PortalMobileNav({ me }: { me: PortalMeResponse | undefined }): JSX.Element {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // A négy fő lap + a PROFIL (a deszktop sidebar láblécének párja).
  const items = [
    ...visiblePortalNav(me).map((n) => ({
      to: n.to,
      label: t(n.labelKey),
      Icon: n.Icon,
      badge: me && n.badge ? n.badge(me.counts) : null,
    })),
    { to: '/portal/profile', label: t('portal.nav.profile'), Icon: UserIcon, badge: null },
  ];

  return (
    <nav
      aria-label={t('portal.nav.aria')}
      className="sidebar-glass app-fixed-bottom fixed inset-x-0 bottom-0 z-30 flex h-[calc(62px+env(safe-area-inset-bottom))] items-center justify-around border-t px-1 pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      {items.map(({ to, label, Icon, badge }) => {
        const active = to === '/portal' ? pathname === '/portal' : pathname.startsWith(to);
        return (
          <Link
            key={to}
            // A tipizált router `to`-ja literál-unió; a nav-config már csak létező route-ot tartalmaz.
            to={to as '/portal'}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'relative flex flex-1 flex-col items-center gap-0.5 px-2 py-1.5',
              active ? 'text-brand' : 'text-muted-foreground',
            )}
          >
            <Icon className="size-5" />
            {badge !== null && badge > 0 && (
              <span className="bg-destructive absolute right-[calc(50%-17px)] top-0.5 inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-1 text-[9.5px] font-semibold text-white">
                {badge}
              </span>
            )}
            <span className="text-[10px] font-semibold">{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
